import type { PropsWithChildren } from 'react';
import React from 'react';

import { Main } from '@atlaskit/navigation-system/layout/main';
import { Root } from '@atlaskit/navigation-system/layout/root';
import { TopNav } from '@atlaskit/navigation-system/layout/top-nav';

import { CompanyHubTopNavigation } from '@confluence/app-navigation/entry-points/companyHubTopNavigation';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { expVal } from '@confluence/feature-experiments';
import { RightSidebarContextProvider } from '@confluence/page-layout-context';

import { PanelLoader } from '../PanelLoader';

/**
 * The {@link https://staging.atlassian.design/components/navigation-system/page-layout/examples | page layout} of the Company Hub app-like experience at Team '25 US.
 *
 * Inspired by `PageLayout` at confluence/next/packages/page-layout/src/PageLayout.tsx
 *
 * @see {@link https://www.figma.com/design/MBdUXhdmgbsIkMkOhQGu5e/Hub-App?node-id=101-52260}
 * @see {@link https://hello.atlassian.net/wiki/spaces/CLI/pages/4856024337/Confluence-less+Team+25+US+Experience}
 */
const CompanyHubPageLayoutComponent = ({ children }: CompanyHubPageLayoutProps) => (
	<RightSidebarContextProvider>
		<Root UNSAFE_dangerouslyHoistSlotSizes>
			<TopNav>
				<CompanyHubTopNavigation />
			</TopNav>
			<Main
				id={expVal('company-hub-hash-navigation', 'isEnabled', false) ? 'AkMainContent' : undefined}
			>
				<ErrorBoundary
					attribution={Attribution.COMPANY_HUB}
					attributes={{ errorBoundaryId: 'company-hub/page-layout/children' }}
				>
					{children}
				</ErrorBoundary>
			</Main>
			<PanelLoader toggleInProductHelp={() => {}} />
		</Root>
	</RightSidebarContextProvider>
);

type CompanyHubPageLayoutProps = PropsWithChildren<{}>;

export const CompanyHubPageLayout = withErrorBoundary({
	attribution: Attribution.COMPANY_HUB,
	attributes: {
		// Inspired by `PageLayout` and `PageLayoutInner` at confluence/next/packages/page-layout/src/PageLayout.tsx
		errorBoundaryId: 'company-hub/page-layout',
	},
})(CompanyHubPageLayoutComponent);
